'use client';

import React from 'react';
import Link from 'next/link';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export default function ItemRemovalService() {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);

  const services = [
    { title: 'Standard Item Removal', price: '$8.00', description: 'For larger items or three or more objects, our Standard Item Removal service is ideal for clearing clutter and distractions. This includes furniture, construction waste, large rugs, or even people — everything can be digitally removed to create a clean, fresh space.', icon: '🪑', turnaround: '4–12 Hours', examples: ['Furniture', 'Construction waste', 'Large rugs', 'People', 'Vehicles', 'Equipment'] },
    { title: 'Minor Item Removal', price: '$4.00', description: 'Eliminate small distractions! If you have one or two items that you could quickly pick up and remove yourself — like an electrical cord, bath towel, pot plant, or "For Sale" sign — our Minor Item Removal service is the perfect solution.', icon: '🧹', turnaround: '4–12 Hours', examples: ['Electrical cords', 'Bath towels', 'Pot plants', 'For Sale signs', 'Small decor items', 'Personal items'] },
    { title: 'Standard Virtual Clean', price: '$8.00', description: 'No need for expensive pressure washing or renovations! Transform your property images with our Standard Virtual Cleaning service, designed to digitally clean larger surfaces, remove bigger stains, and eliminate visible wear.', icon: '✨', turnaround: '4–12 Hours', examples: ['Pressure washing effects', 'Large stains', 'Visible wear', 'Dirty surfaces', 'Graffiti', 'Wall marks'] },
    { title: 'Minor Virtual Clean', price: '$4.00', description: "Need a light clean? Our Minor Virtual Clean service tackles small imperfections that might go unnoticed in person but can stand out in photos. Whether it's smudges, scuffs, minor stains, or wrinkled covers, we refine your image for a cleaner, more polished look.", icon: '🧽', turnaround: '4–12 Hours', examples: ['Smudges', 'Scuffs', 'Minor stains', 'Wrinkled covers', 'Dust', 'Small marks'] },
  ];

  const comboService = {
    title: 'Item Removal + Virtual Clean',
    price: '$8.00',
    description: "This bundled solution is perfect for effortlessly eliminating distracting objects and digitally cleaning surfaces simultaneously. Whether you need to remove large items like bulky furniture or minor nuisances such as smudges and scuffs, our expert editors refine every detail. The result is a pristine, clutter-free image that showcases your property's true potential and captivates prospective buyers.",
    icon: '🎯',
    turnaround: '4–12 Hours',
    badge: 'BEST VALUE',
  };

  const benefits = [
    { title: 'Declutter a Space', description: 'Ensure your listing photos showcase a clean and clutter-free home, allowing the property to shine at its best.', icon: '🏠' },
    { title: 'Enhance Visual Appeal', description: 'Erase distractions in property photos for a positive first impression on buyers or tenants. Digitally remove anything impacting a love-at-first-sight experience with ease.', icon: '👁️' },
    { title: 'Thought You Had the Perfect Shot?', description: "Getting ready to upload your latest listing and find something that shouldn't be in the shot? No problem, we can digitally remove it for you.", icon: '📸' },
    { title: 'Prepare for Virtual Staging', description: "For listings with distracting decor or unappealing furniture, we'll digitally remove and virtually stage photos to help buyers or tenants envision themselves in the property.", icon: '🎨' },
  ];

  const stats = [
    { metric: '95%', label: 'Client Satisfaction', description: 'Clients love our item removal results' },
    { metric: '24hr', label: 'Fast Turnaround', description: 'Quick delivery without compromising quality' },
    { metric: '100%', label: 'Quality Guarantee', description: 'Unlimited revisions until perfect' },
    { metric: '$4+', label: 'Starting Price', description: 'Affordable professional editing' },
  ];

  const beforeAfterExamples = [
    { before: '/images/gallery/item-removal-living-before.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/item-removal-living-after.webp?auto=compress&cs=tinysrgb&w=600', title: 'Standard Item Removal', turnaround: '4–12 Hours' },
    { before: '/images/gallery/minorbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/minoraf.webp?auto=compress&cs=tinysrgb&w=600', title: 'Minor Item Removal', turnaround: '4–12 Hours' },
    { before: '/images/gallery/item-removal-bedroom-before.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/item-removal-bedroom-after.webp?auto=compress&cs=tinysrgb&w=600', title: 'Virtual Clean', turnaround: '4–12 Hours' },
  ];

  const pricingTiers = [
    { name: 'Minor Item Removal', price: '$4', unit: '/image', tag: 'Small distractions', features: ['1-2 small items', '4–12 hour turnaround', 'Unlimited revisions'], popular: false },
    { name: 'Minor Virtual Clean', price: '$4', unit: '/image', tag: 'Light cleaning', features: ['Small imperfections', '4–12 hour turnaround', 'Professional quality'], popular: false },
    { name: 'Standard Item Removal', price: '$8', unit: '/image', tag: 'Large items & clutter', features: ['3+ items or large objects', 'Priority processing', 'Expert editors'], popular: true },
    { name: 'Combo Service', price: '$8', unit: '/image', tag: 'Remove + Clean', features: ['Item removal + cleaning', 'Best value bundle', 'Complete transformation'], popular: false },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  const Badge = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center bg-white/[0.04] border border-white/[0.12] text-purple-200 px-6 py-2 rounded-full text-sm font-bold">
      {children}
    </span>
  );

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17] border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
                <span className="text-sm">🧹</span>
                <span>Starting at $4.00 per image</span>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                  Item removal &amp;{' '}
                  <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                    virtual cleaning.
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Remove unwanted items from your photos and declutter a space. Ensure your listing photos showcase a
                  clean, clutter-free home so the property shines at its best.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                    Get Started Now
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#services">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    View Services
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-1">{stat.metric}</div>
                    <div className="text-xs font-semibold text-white mb-1">{stat.label}</div>
                    <div className="text-xs text-gray-500 leading-tight">{stat.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="relative h-[450px] lg:h-[550px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
                <ProtectedAppImage
                  src="/images/gallery/item-removal-living-after.webp?auto=compress&cs=tinysrgb&w=1200"
                  alt="Item removal and virtual cleaning"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-3 rounded-lg font-bold text-sm shadow-lg shadow-purple-500/30">
                  AFTER CLEANING
                </div>
                <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-5 py-3 rounded-lg font-bold text-sm">
                  4–12 HOUR TURNAROUND
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BENEFITS ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Why remove items &amp; clean digitally?</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Erase distractions in property photos for a positive first impression on buyers or tenants. Digitally
              remove anything impacting a love-at-first-sight experience with ease.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((item, index) => (
              <div key={index} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-3xl mb-5 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section id="services" className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>OUR SERVICES</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Choose your service</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              From minor touch-ups to major transformations, we have the perfect solution for every property photo
              challenge.
            </p>
          </div>

          {/* Combo Service - Featured */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-purple-600/25 to-blue-600/15 border-2 border-purple-500/40 shadow-[0_30px_80px_rgba(124,58,237,0.2)]">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">
                {comboService.badge}
              </div>
              <div className="flex items-start gap-6">
                <div className="text-5xl">{comboService.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="text-2xl lg:text-3xl font-bold text-white">{comboService.title}</h3>
                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent">{comboService.price}</span>
                    <span className="text-xs font-bold bg-white/[0.06] border border-white/10 text-purple-200 px-3 py-1 rounded-full">{comboService.turnaround}</span>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{comboService.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Individual Services Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div key={index} className="group bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="text-4xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-bold text-white">{service.title}</h3>
                      <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{service.price}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{service.turnaround}</span>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                    <div className="border-t border-white/[0.08] pt-4">
                      <p className="text-sm font-semibold text-gray-300 mb-2">Examples:</p>
                      <div className="flex flex-wrap gap-2">
                        {service.examples.map((example, i) => (
                          <span key={i} className="text-xs bg-purple-500/10 border border-purple-500/20 text-purple-300 px-3 py-1 rounded-full">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BEFORE / AFTER ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">See the transformation</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Real before and after examples showcasing the power of professional item removal and virtual cleaning.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {beforeAfterExamples.map((example, index) => (
              <div key={index} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.before} alt={`${example.title} - Before`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded">BEFORE</div>
                  </div>
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.after} alt={`${example.title} - After`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30">AFTER</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-white mb-2">{example.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{example.turnaround}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GET STARTED ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">Ready to get started?</h2>
          <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            Sign up now and transform your property photos with professional item removal and virtual cleaning. No
            credit card required to create an account.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href={signupUrl}>
              <button className="px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                Create Free Account
              </button>
            </Link>
            <Link href="/auth/signin">
              <button className="px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                Sign In
              </button>
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6">Already have an account? Sign in to start your project</p>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Transparent, affordable pricing</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Professional item removal and virtual cleaning at unbeatable prices. No hidden fees, no subscriptions,
              no surprises.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-start">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={
                  tier.popular
                    ? 'relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 backdrop-blur-sm border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.25)] lg:scale-105'
                    : 'bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300'
                }
              >
                {tier.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-3">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className={tier.popular ? 'text-5xl font-bold text-white' : 'text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent'}>{tier.price}</span>
                    <span className={tier.popular ? 'text-gray-300' : 'text-gray-500'}>{tier.unit}</span>
                  </div>
                  <p className={`text-sm mt-3 ${tier.popular ? 'text-gray-300' : 'text-gray-500'}`}>{tier.tag}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.popular ? 'text-purple-300' : 'text-purple-400'}`} />
                      <span className={tier.popular ? 'text-white' : 'text-gray-400'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link href={signupUrl}>
                  <button
                    className={
                      tier.popular
                        ? 'w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5'
                        : 'w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all'
                    }
                  >
                    {tier.popular ? 'Start Project' : 'Get Started'}
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== QUALITY GUARANTEE ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Quality{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">guaranteed.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              We stand behind our work with a 100% satisfaction guarantee. If you&apos;re not happy, we&apos;ll make it
              right or refund your money.
            </p>
            <ul className="space-y-4">
              {[
                '4–12 hour turnaround time',
                'Unlimited free revisions',
                'Expert editors with 10+ years experience',
                'Natural-looking results',
                '100% satisfaction guarantee',
                'Secure file handling & privacy',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
              <ProtectedAppImage src="/images/gallery/ondorevsbefore.webp?auto=compress&cs=tinysrgb&w=1200" alt="Quality guarantee" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Ready to perfect your{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">photos?</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Join thousands of real estate professionals who trust Shashio for professional item removal and virtual
              cleaning. Get started today and showcase your properties at their absolute best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Your Project
                </button>
              </Link>
              <Link href="/homepage#pricing">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  View Pricing
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
